import { Dialog, DialogContent, DialogActions, Checkbox, Divider } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import OverlayLoader from "components/OverlayLoader";

import { FaUser, FaClock, FaHistory, FaCheck } from "react-icons/fa";
import useCommon from "hooks/useCommon";
import { useEffect } from "react";
import { getRequestDetails } from "store/slice/requestSlice";
import { resetRequestDetailsState } from "store/slice/requestSlice";
import dayjs from "dayjs";

const RequestDetailDialog = ({ open, onClose, request, onToggleComplete }) => {
  if (!request) return null;

  const { STORE, DISPATCH } = useCommon();

  const {
    requestLogs = [],
    detailsLoading,
    bulkCompleteLoading,
  } = STORE((state) => state.requests);

  useEffect(() => {
    if (open && request?.id) {
      DISPATCH(resetRequestDetailsState());
      DISPATCH(getRequestDetails(request.id)).unwrap();
    }
  }, [open, request?.id]);

  const logs = requestLogs || [];
  const isCompleted = request.status === "Completed";

  const formatChangeValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "object" ? item.name || item.label || item._id : item))
        .join(", ");
    }

    if (typeof value === "object") {
      return value.name || value.label || value._id || JSON.stringify(value);
    }

    return String(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {detailsLoading && (
        <OverlayLoader open={detailsLoading} message="Loading request details..." />
      )}

      <DialogContent>
        <VuiBox display="flex" flexDirection="column" gap={2}>
          <VuiBox
            p={2}
            borderRadius="lg"
            sx={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start">
              <VuiBox>
                <VuiTypography variant="h5" fontWeight="bold">
                  {request.room}
                </VuiTypography>

                <VuiTypography variant="caption">{request.guestName}</VuiTypography>
              </VuiBox>
            </VuiBox>

            <VuiBox mt={1.5} display="flex" alignItems="center" gap={2}>
              <VuiBox display="flex" alignItems="center" gap={0.5}>
                <FaUser size={12} color="#9ca3af" />
                <VuiTypography variant="caption">
                  Created by {request.createdBy?.name || "System"}
                </VuiTypography>
              </VuiBox>

              <VuiBox display="flex" alignItems="center" gap={0.5}>
                <FaClock size={12} color="#9ca3af" />
                <VuiTypography variant="caption">
                  {request.createdAt
                    ? dayjs(request.createdAt).format("MMM D, YYYY • h:mm A")
                    : "-"}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>

          {/* ================= REQUEST CONTENT ================= */}
          <VuiBox
            p={2}
            borderRadius="lg"
            sx={{
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <VuiTypography variant="button" fontWeight="bold" display="block">
              {request.title}
            </VuiTypography>

            <VuiTypography
              variant="caption"
              display="block"
              mt={1}
              sx={{
                lineHeight: 1.6,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "pre-line",
              }}
            >
              {request.description}
            </VuiTypography>
          </VuiBox>

          {/* ================= ASSIGNED USERS ================= */}
          <VuiBox>
            <VuiTypography variant="caption" mb={1}>
              Assigned To
            </VuiTypography>

            <VuiBox display="flex" gap={1} flexWrap="wrap">
              {request.assignedTo?.length > 0 ? (
                request.assignedTo.map((u) => (
                  <VuiBox
                    key={u.id}
                    px={1.5}
                    py={0.6}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    gap={0.6}
                    sx={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <FaUser size={10} color="#9ca3af" />

                    <VuiTypography variant="caption">{u.name}</VuiTypography>
                  </VuiBox>
                ))
              ) : (
                <VuiBox
                  px={1.5}
                  py={0.6}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  gap={0.6}
                  sx={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <VuiTypography variant="caption">Unassigned</VuiTypography>
                </VuiBox>
              )}
            </VuiBox>
          </VuiBox>

          {/* ================= ACTIVITY ================= */}

          <VuiBox mt={1} position="relative">
            <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
              <FaHistory size={14} color="#9ca3af" />
              <VuiTypography variant="button">Activity</VuiTypography>
            </VuiBox>

            <VuiBox
              px={2}
              py={2}
              borderRadius="md"
              sx={{
                minHeight: "140px",
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.06)",
                position: "relative",

                maxHeight: "320px",
                overflowY: "auto",
                pr: 1,

                position: "relative",
              }}
            >
              {!detailsLoading &&
                (logs.length === 0 ? (
                  <VuiTypography variant="caption">No activity yet</VuiTypography>
                ) : (
                  <VuiBox display="flex" flexDirection="column" gap={1}>
                    {logs.map((log) => (
                      <VuiBox
                        key={log._id}
                        display="flex"
                        // gap={1.5}
                        sx={{
                          pb: 1.5,
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                          "&:last-child": { borderBottom: "none", pb: 0 },
                        }}
                      >
                        <VuiBox width="100%">
                          {/* Title */}
                          <VuiBox display="flex" alignItems="center" gap={0.8}>
                            <VuiBox
                              width="8px"
                              height="8px"
                              borderRadius="50%"
                              flexShrink={0}
                              sx={{
                                background: "#00c8ff",
                                boxShadow: "0 0 8px rgba(0,200,255,0.55)",
                              }}
                            />

                            <VuiTypography variant="caption" fontWeight="medium">
                              {log.eventType === "edited"
                                ? `Request updated by ${log.actor?.name || "System"}`
                                : log.eventType === "created"
                                  ? `Request created by ${log.actor?.name || "System"}`
                                  : log.eventType === "completed"
                                    ? `Request completed by ${log.actor?.name || "System"}`
                                    : log.eventType === "status_changed"
                                      ? `Status updated by ${log.actor?.name || "System"}`
                                      : `${log.eventType} by ${log.actor?.name || "System"}`}
                            </VuiTypography>
                          </VuiBox>

                          {/* Date */}
                          <VuiTypography
                            variant="caption"
                            sx={{ display: "block", mt: 0.25, fontSize: "11px" }}
                          >
                            {dayjs(log.createdAt).format("MMM D, YYYY • h:mm A")}
                          </VuiTypography>

                          {/* Changes */}
                          {log.changes?.length > 0 && (
                            <VuiBox my={1} display="flex" flexDirection="column" gap={0.7}>
                              {log.changes
                                .filter(
                                  (change) => !["completedAt", "completedBy"].includes(change.field)
                                )
                                .map((change, idx) => {
                                  const isValidDate = (value) => {
                                    if (!value || typeof value !== "string") return false;

                                    return dayjs(value).isValid();
                                  };

                                  const isDateField =
                                    change.field?.toLowerCase().includes("date") ||
                                    isValidDate(change.from) ||
                                    isValidDate(change.to);

                                  const fromValue = isDateField
                                    ? dayjs(change.from).format("MMM D, YYYY")
                                    : formatChangeValue(change.from);

                                  const toValue = isDateField
                                    ? dayjs(change.to).format("MMM D, YYYY")
                                    : formatChangeValue(change.to);

                                  return (
                                    <VuiBox
                                      key={idx}
                                      px={1.2}
                                      py={0.5}
                                      borderRadius="md"
                                      sx={{
                                        background: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(255,255,255,0.05)",
                                      }}
                                    >
                                      <VuiTypography
                                        variant="body2"
                                        sx={{
                                          fontSize: "12px",
                                          lineHeight: "22px",
                                          wordSpacing: "1.5px",
                                          whiteSpace: "normal",
                                        }}
                                      >
                                        <strong>{change.field}</strong> changed from{" "}
                                        <span style={{ color: "#fff" }}>{fromValue}</span> to{" "}
                                        <span style={{ color: "#fff" }}>{toValue}</span>
                                      </VuiTypography>
                                    </VuiBox>
                                  );
                                })}
                            </VuiBox>
                          )}
                        </VuiBox>
                      </VuiBox>
                    ))}
                  </VuiBox>
                ))}
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </DialogContent>

      {/* ================= FOOTER ================= */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <VuiBox display="flex" justifyContent="flex-end" gap={1} width="100%">
          <VuiButton variant="outlined" color="primary" onClick={onClose}>
            Close
          </VuiButton>

          <VuiButton
            variant="contained"
            color="success"
            startIcon={!isCompleted && <FaCheck size={12} />}
            disabled={isCompleted || bulkCompleteLoading}
            onClick={async () => {
              const success = await onToggleComplete?.(request);

              if (success) {
                onClose?.();
              }
            }}
          >
            {bulkCompleteLoading ? "Processing..." : isCompleted ? "Completed" : "Mark As Done"}
          </VuiButton>
        </VuiBox>
      </DialogActions>
    </Dialog>
  );
};

export default RequestDetailDialog;
